import Paper from '@material-ui/core/Paper';
import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';

import { Alert, Box, Button, Card, Checkbox, Col, DataGrid, FormControlLabel, Link, Row, Select, TextField, Typography } from 'components/AORedesign';
import {
  AGENCY_ADMIN_GET_POTENTIAL_AGENCIES,
  USER_ADMIN_CHALLENGE_QUESTIONS,
  USER_ADMIN_CREATE_USER,
  USER_ADMIN_GET_USER_INFO,
  USER_ADMIN_PERMITTED_AGENCY_CODES,
  USER_ADMIN_PERMITTED_GROUPS,
  USER_ADMIN_SET_NEW_PASSWORD,
  USER_ADMIN_UPDATE_USER,
} from '../../constants/api';
import featureFlags from '../../constants/featureFlags';
import agentContext from '../../contexts/agent.context';
import useFetchSelectMenuItems from '../../hooks/useFetchSelectMenuItems';
import AOTransferList from '../AOTransferList/AOTransferList';
import { Feature } from '../Feature';

const INITIAL_ADD_USER_FORM_DATA = {
  givenName: '', // first name
  sn: '', // last name
  mail: '', // email
  artAgentID: '', // amic agency number
  modId: '', // mod id
  ininetViewAll: false, // view all boolean
  assignedAgencies: [], // agencies
  challengeQuestion: '', // challenge question
  challengeAnswer: '', // challenge response
  memberGroups: [], // groups
  npnStatus: '', // npn license information
  npnNumber: '', // npn license number
};

const EDIT_USER_KEY_MAPPING = {
  givenName: 'givenName',
  sn: 'sn',
  mail: 'mail',
  artAgentID: 'artAgentId',
  modId: 'ininetmodID',
  ininetViewAll: 'ininetViewAll',
  assignedAgencies: 'assignedAgencies',
  challengeQuestion: 'ininetChQuest',
  challengeAnswer: 'ininetChAns',
  memberGroups: 'memberGroups',
  accountDisabled: 'accountDisabled',
  npnNumber: 'npnNumber',
  npnStatus: 'npnStatus',
};

const INITIAL_ERROR_DATA = {
  givenName: null,
  sn: null,
  mail: null,
  artAgentID: null,
  modId: null,
  challengeAnswer: null,
  npnStatus: null,
  npnNumber: null,
};

const INITIAL_ALERT_DATA = { viewAll: false, searchAgency: false, submitAlert: false, nullGroup: false, passwordReset: false, licenseAlert: false };

const FIELD_VALIDATIONS = {
  givenName: {
    required: 'First Name is a required field. Please enter it now.',
    validationMessage: 'Special characters are NOT allowed in First Name',
    pattern: /^[A-Za-z0-9]+$/,
  },
  sn: {
    required: 'Last name is a required field. Please enter it now.',
    validationMessage: 'Special characters are NOT allowed in Last Name',
    pattern: /^[A-Za-z0-9]+$/,
  },
  mail: {
    required: 'Email is a required field. Please enter it now.',
    validationMessage: 'Please enter a valid email address in the form of user@domain.com',
    pattern:
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  },
  artAgentID: {
    required: false,
    validationMessage: 'Please enter 7 digit AMIC Agency Number',
    pattern: /^\d{7}$/,
  },
  modId: {
    required: false,
    validationMessage: 'Alphabetic character followed by alphanumeric characters.',
    pattern: /^[a-zA-Z][a-zA-Z0-9]{2}$/,
  },
  challengeAnswer: {
    required: 'Challenge Response is a required field. Please enter it now.',
    validationMessage: 'Special characters are NOT allowed in Challenge Response',
    pattern: /[A-Za-z0-9\\?\\-\\.\x27\x20]+$/,
  },
  npnStatus: {
    required: true,
    validationMessage: 'License information is a required field.',
  },
  npnNumber: {
    validationMessage: 'NPN Number must be 5-10 numeric digits and cannot start with 0.',
    pattern: /^[1-9][0-9]{4,9}$/,
  },
};

// Column definition for DataGrid
const potentialAgenciesColumns = [
  {
    field: 'agencyId',
    headerName: 'Agency Id',
    width: 200,
  },
  {
    field: 'agencyName',
    headerName: 'Agency Name',
    width: 450,
  },
  {
    field: 'agencyCity',
    headerName: 'Agency City',
    width: 200,
  },
  {
    field: 'agencyState',
    headerName: 'Agency State',
    width: 200,
  },
];

const NPN_LICENSE_INFORMATION_OPTIONS = [
  {
    label: 'User is licensed',
    value: 'LICENSED',
  },
  {
    label: 'User is not licensed',
    value: 'UNLICENSED',
  },
  {
    label: 'User is working on obtaining license',
    value: 'PENDING',
  },
];

const UserAdminAddEditUserInfo = ({ title, userDetails, setUserDetails }) => {
  const [formData, setFormData] = useState(INITIAL_ADD_USER_FORM_DATA);
  const formDataRef = useRef();
  const [challengeQuestionOptions, setChallengeQuestionOptions] = useState([]);
  const challengeQuestionsRef = useRef([]);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Internal - Agency name
  const [potentialAgencies, setPotentialAgencies] = useState(null);
  const [agencyNameInfo, setAgencyNameSearchValue] = useState('');
  const [selectionModel, setSelectionModel] = useState([]);

  // External - Agency (multiple) selection
  const [agenciesSelected, setSelectedAgencies] = useState([]);
  const [externalUserAgencies, setExternalUserAgencies] = useState(null);
  const permittedGroupsRef = useRef([]);

  // Groups - TransferList
  const [availableUserGroups, setAvailableUserGroups] = useState([]);
  const [checkedUserGroups, setCheckedUserGroups] = useState([]);
  const [selectedUserGroups, setSelectedUserGroups] = useState([]);

  const [fieldErrors, setFieldErrors] = useState(INITIAL_ERROR_DATA);
  const [alerts, setAlerts] = useState(INITIAL_ALERT_DATA);

  const USER_ADMIN_ADD_VIEW = userDetails === undefined;
  const USER_ADMIN_EDIT_VIEW = !USER_ADMIN_ADD_VIEW;
  const { UserID: userId, FeatureFlags } = useContext(agentContext);
  const UserID = userId?.split('@')[0];

  // Get challenge questions
  useFetchSelectMenuItems(USER_ADMIN_CHALLENGE_QUESTIONS, (response) => {
    const { data } = response;
    challengeQuestionsRef.current = data ?? [];
    setChallengeQuestionOptions(
      data.map((question) => ({
        label: question,
        value: question,
      })),
    );
    setFormData((prevState) => ({ ...prevState, challengeQuestion: data[0] }));
  });

  // Get available groups
  useFetchSelectMenuItems(USER_ADMIN_PERMITTED_GROUPS, (response) => {
    const { data } = response;
    permittedGroupsRef.current = data ?? [];
    const groups = permittedGroupsRef.current.map((o) => o.value);
    setAvailableUserGroups(groups);
  });

  // Handle userDetails data object for edit user
  useEffect(() => {
    if (userDetails) {
      // Store master copy
      const editFormData = { ...INITIAL_ADD_USER_FORM_DATA };
      for (const key in EDIT_USER_KEY_MAPPING) {
        editFormData[key] = userDetails[EDIT_USER_KEY_MAPPING[key]];
      }
      formDataRef.current = editFormData;
      setFormData(editFormData);
    }
  }, [userDetails]);

  useEffect(() => {
    (async () => {
      if (!FeatureFlags[featureFlags.ACCESS_VIEW_ALL]) {
        try {
          const getPermittedAgencyCodesUrl = `${USER_ADMIN_PERMITTED_AGENCY_CODES}`;
          const { data } = await axios.get(getPermittedAgencyCodesUrl);
          setExternalUserAgencies(data);
        } catch (error) {
          console.error(error);
          setAlerts((prevState) => ({ ...prevState, searchAgency: true }));
        }
      }
    })();
  }, []);

  // External user (multiple) agency selection handler
  const handleExternalAgencyMultipleSelection = (event) => {
    const { options } = event.target;
    const value = [];
    for (let i = 0, l = options.length; i < l; i += 1) {
      if (options[i].selected) {
        value.push(options[i].value);
      }
    }
    setSelectedAgencies(value);
    setFormData({ ...formData, assignedAgencies: value });
  };

  // Select onChange handler
  const handleSelectOnChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  };

  // Toggle view all
  const handleClickInternalUser = (e) => {
    const { checked } = e.target;
    setFormData((prevState) => ({ ...prevState, ininetViewAll: checked }));
    setAlerts((prevState) => ({ ...prevState, viewAll: checked }));
  };

  // Agency name search SEARCH onClick handler
  const handleSearchAgencyNameOnClick = async () => {
    try {
      setAlerts((prevState) => ({ ...prevState, searchAgency: false }));
      const details = await axios.get(AGENCY_ADMIN_GET_POTENTIAL_AGENCIES, {
        params: {
          userName: UserID,
          agencyName: agencyNameInfo,
        },
      });
      setPotentialAgencies(details.data);
    } catch (error) {
      console.error(error);
      setPotentialAgencies(null);
      setAlerts((prevState) => ({ ...prevState, searchAgency: true }));
    }
  };

  // Agency name search CLEAR onClick handler
  const handleClearAgencyNameOnClick = () => {
    setAgencyNameSearchValue('');
    setSelectionModel([]);
    setPotentialAgencies(null);
  };

  // Internal user agency name selection handler
  const handleSelectionModelOnChange = (model) => {
    setSelectionModel(model);
    setFormData({ ...formData, assignedAgencies: model });
  };

  // TransferList component left setter handler
  const handleUserGroupsSetLeft = (e) => {
    setAvailableUserGroups(e);
  };

  // TransferList component right setter handler
  const handleUserGroupsSetRight = (e) => {
    setSelectedUserGroups(e);
    setFormData({ ...formData, memberGroups: e });
  };

  const validateField = (e, setValidationError) => {
    const { name, value } = e.target;
    const regexPattern = new RegExp(FIELD_VALIDATIONS[name].pattern);
    if (!value && FIELD_VALIDATIONS[name].required) {
      setValidationError(FIELD_VALIDATIONS[name].required);
    } else if (value && !regexPattern.test(value)) {
      setValidationError(FIELD_VALIDATIONS[name].validationMessage);
    } else {
      setValidationError(null);
    }
  };

  const validateNpnField = () => {
    const regexPattern = new RegExp(FIELD_VALIDATIONS.npnNumber.pattern);
    if (formData.npnStatus === '' && FIELD_VALIDATIONS.npnStatus.required) {
      setFieldErrors((prevState) => ({ ...prevState, npnStatus: FIELD_VALIDATIONS.npnStatus.validationMessage }));
      setAlerts((prevState) => ({ ...prevState, licenseAlert: true }));
      return false;
    } else if (formData.npnStatus === NPN_LICENSE_INFORMATION_OPTIONS[0].value && !regexPattern.test(formData.npnNumber)) {
      setFieldErrors((prevState) => ({ ...prevState, npnNumber: FIELD_VALIDATIONS.npnNumber.validationMessage }));
      setAlerts((prevState) => ({ ...prevState, licenseAlert: true }));
      return false;
    }
    return true;
  };

  const handleAddUserSubmitOnClick = async (event) => {
    event.preventDefault();
    setAlerts(INITIAL_ALERT_DATA);
    const formDataPayload = { ...formData };

    if (!validateNpnField()) {
      return;
    }

    try {
      setSubmitLoading(true);
      if (formData.ininetViewAll === false && formData.assignedAgencies.length === 0) {
        setAlerts((prevState) => ({ ...prevState, nullAgency: true }));
        return;
      } else {
        if (formData.ininetViewAll === true) {
          delete formDataPayload.assignedAgencies;
        }
        setAlerts((prevState) => ({ ...prevState, nullAgency: false }));
      }

      if (formData.memberGroups.length < 1) {
        setAlerts((prevState) => ({ ...prevState, nullGroup: true }));
        return;
      } else {
        setAlerts((prevState) => ({ ...prevState, nullGroup: false }));
      }

      // Only include npnNumber if user is licensed
      if (formData.npnStatus !== NPN_LICENSE_INFORMATION_OPTIONS[0].value) {
        delete formDataPayload.npnNumber;
      }

      // find group 'key' based on 'value' and send to API
      if (formData.memberGroups.length > 0) {
        const groupArray = formData.memberGroups.reduce((groupKeys, element) => {
          const item = permittedGroupsRef.current.find((item) => item.value === element);
          if (item && item.key) {
            return [...groupKeys, item.key];
          } else {
            return groupKeys;
          }
        }, []);
        formDataPayload.memberGroups = groupArray;
      }

      const response = await axios.post(USER_ADMIN_CREATE_USER, formDataPayload);
      if (response.data.cn !== undefined && response.data.cn !== null) {
        handleAddUserClearFormOnClick();
        setAlerts((prevState) => ({
          ...prevState,
          submitAlert: {
            severity: 'success',
            message: `New User Created UserName : ${response.data.cn}. Please wait at least one hour before making further updates.`,
          },
        }));
      } else if (response.data.duplicateEmail) {
        setAlerts((prevState) => ({
          ...prevState,
          submitAlert: {
            severity: 'error',
            message: `Unable to proceed, email already exists. If you already have a Username, please try resetting your password from Agent’s Only login page. For additional assistance, please contact your Agency Administrator or Customer Service Department at (877) 9ASKMSA (877-927-5672) or customer@msagroup.com.`,
          },
        }));
      } else {
        setAlerts((prevState) => ({
          ...prevState,
          submitAlert: {
            severity: 'error',
            message: `Failed due to API Response,please contact your Agency Administrator or Customer Service Department at (877) 9ASKMSA (877-927-5672) or customer@msagroup.com.`,
          },
        }));
      }
    } catch (error) {
      console.error(error);
      setAlerts((prevState) => ({
        ...prevState,
        submitAlert: {
          severity: 'error',
          message: `Unable to add user ${formData.givenName}`,
        },
      }));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEditUserSubmitOnClick = async (event) => {
    event.preventDefault();
    setAlerts(INITIAL_ALERT_DATA);

    if (!validateNpnField()) {
      return;
    }

    const formDataPayload = { ...userDetails };

    for (const key in formData) {
      // Check if mapping exists, use it if it does, otherwise use the key directly
      const mappedKey = EDIT_USER_KEY_MAPPING[key] || key;
      formDataPayload[mappedKey] = formData[key];
    }

    try {
      setSubmitLoading(true);
      const response = await axios.put(USER_ADMIN_UPDATE_USER, formDataPayload);
      if (response.data === 'Success' || response.status === 204) {
        setAlerts((prevState) => ({
          ...prevState,
          submitAlert: {
            severity: 'success',
            message: `${userDetails?.cn} updated. Please wait at least one hour before making further updates.`,
          },
        }));

        const userName = userDetails?.name;
        const getUserDetailsUrl = `${USER_ADMIN_GET_USER_INFO}/${userName}`;
        const details = await axios.get(getUserDetailsUrl);
        setUserDetails(details.data);
      } else if (response.data === 'DuplicateEmail' || response.status === 409) {
        setAlerts((prevState) => ({
          ...prevState,
          submitAlert: {
            severity: 'error',
            message: `Unable to proceed, email already exists. If you already have a Username, please try resetting your password from Agent's Only login page. For additional assistance, please contact your Agency Administrator or Customer Service Department at (877) 9ASKMSA (877-927-5672) or customer@msagroup.com.`,
          },
        }));
      } else if (response.status === 403) {
        setAlerts((prevState) => ({
          ...prevState,
          submitAlert: {
            severity: 'error',
            message: `Forbidden - Missing necessary permissions, please contact customer service.`,
          },
        }));
      } else if (response.status === 401) {
        setAlerts((prevState) => ({
          ...prevState,
          submitAlert: {
            severity: 'error',
            message: `Unauthorized - You are not authorized for this operation, please contact customer service.`,
          },
        }));
      } else if (response.status === 400) {
        setAlerts((prevState) => ({
          ...prevState,
          submitAlert: {
            severity: 'error',
            message: `Bad Request - There was a problem processing this request. Please try again later or contact customer service.',
`,
          },
        }));
      } else {
        setAlerts((prevState) => ({
          ...prevState,
          submitAlert: {
            severity: 'error',
            message: response.data?.error,
          },
        }));
      }
    } catch (error) {
      console.error(error);
      if (error.response && error.response.status === 409) {
        setAlerts((prevState) => ({
          ...prevState,
          submitAlert: {
            severity: 'error',
            message: `Unable to proceed, email already exists. If you already have a Username, please try resetting your password from Agent's Only login page. For additional assistance, please contact your Agency Administrator or Customer Service Department at (877) 9ASKMSA (877-927-5672) or customer@msagroup.com.`,
          },
        }));
      } else {
        setAlerts((prevState) => ({
          ...prevState,
          submitAlert: {
            severity: 'error',
            message: `Unable to update user ${userDetails?.cn}`,
          },
        }));
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleAddUserClearFormOnClick = () => {
    setFormData({ ...INITIAL_ADD_USER_FORM_DATA, challengeQuestion: challengeQuestionsRef.current[0] });
    setFieldErrors(INITIAL_ERROR_DATA);
    handleClearAgencyNameOnClick();

    setCheckedUserGroups([]);
    setSelectedUserGroups([]);
    setAvailableUserGroups(permittedGroupsRef.current.map((o) => o.value));
    setAlerts(INITIAL_ALERT_DATA);
  };

  const handleEditUserClearFormOnClick = () => {
    setFormData(formDataRef.current);
    setAlerts(INITIAL_ALERT_DATA);
  };

  const isSubmitDisabled = () => {
    const errors = Object.values(fieldErrors);
    const hasError = errors.some((err) => typeof err === 'string');
    const { givenName, sn, mail, challengeAnswer } = formData;

    return submitLoading || hasError || !givenName || !sn || !mail || !challengeAnswer;
  };

  const handleResetPasswordOnClick = async () => {
    setAlerts(INITIAL_ALERT_DATA);
    try {
      const resetPasswordResponse = await axios.put(`${USER_ADMIN_SET_NEW_PASSWORD}/${userDetails?.cn}/${userDetails?.mail}`);
      if (resetPasswordResponse?.status === 200) {
        setAlerts((prevState) => ({
          ...prevState,
          passwordReset: {
            severity: 'success',
            message: `The password for account ${userDetails?.cn} has been changed.`,
          },
        }));
      }
    } catch (error) {
      console.error(error);
      if (error?.response?.status === 403) {
        setAlerts((prevState) => ({
          ...prevState,
          passwordReset: { severity: 'error', message: 'Forbidden - Missing necessary permissions, please contact customer service.' },
        }));
      } else {
        setAlerts((prevState) => ({
          ...prevState,
          passwordReset: { severity: 'error', message: `Password reset failed ${error}` },
        }));
      }
    }
  };

  return (
    <Feature allowed={featureFlags.ACCESS_AGENCY_SECURITY_USER_ADMIN}>
      <Card>
        <Box mb={4}>
          <Typography variant="h5">{title}</Typography>
        </Box>

        <Row spacing={3}>
          <Col xs={6}>
            <Paper variant="outlined" square style={{ boxSizing: 'border-box', height: '100%' }}>
              <Box p={2}>
                <Box px={1}>
                  <Alert severity="info">Special Characters are NOT allowed in First and Last Name fields</Alert>
                </Box>

                <Box px={1} pt={2}>
                  <TextField
                    size="small"
                    id={'first-name'}
                    name={'givenName'}
                    value={formData.givenName}
                    onChange={(e) => {
                      setFormData({ ...formData, givenName: e.target.value });
                      validateField(e, (error) => setFieldErrors((prevState) => ({ ...prevState, givenName: error })));
                    }}
                    label={'First Name'}
                    placeholder={'Enter First Name'}
                    required
                    fullWidth
                    error={!!fieldErrors.givenName}
                    helperText={fieldErrors.givenName}
                  />
                  <Box mb={4} />
                  <TextField
                    size="small"
                    id={'last-name'}
                    name={'sn'}
                    value={formData.sn}
                    onChange={(e) => {
                      setFormData({ ...formData, sn: e.target.value });
                      validateField(e, (error) => setFieldErrors((prevState) => ({ ...prevState, sn: error })));
                    }}
                    label={'Last Name'}
                    placeholder={'Enter Last Name'}
                    required
                    fullWidth
                    error={!!fieldErrors.sn}
                    helperText={fieldErrors.sn}
                  />
                  <Box mb={4} />
                  <TextField
                    size="small"
                    id={'email'}
                    name={'mail'}
                    value={formData.mail}
                    onChange={(e) => {
                      setFormData({ ...formData, mail: e.target.value });
                      validateField(e, (error) => setFieldErrors((prevState) => ({ ...prevState, mail: error })));
                    }}
                    label={'Email'}
                    placeholder={'Enter Email'}
                    required
                    fullWidth
                    error={!!fieldErrors.mail}
                    helperText={fieldErrors.mail}
                  />
                  <Box mb={4} />
                  <TextField
                    size="small"
                    id={'amic-agency-number'}
                    name={'artAgentID'}
                    value={formData.artAgentID}
                    onChange={(e) => {
                      setFormData({ ...formData, artAgentID: e.target.value });
                      validateField(e, (error) => setFieldErrors((prevState) => ({ ...prevState, artAgentID: error })));
                    }}
                    label={'AMIC Agency Number'}
                    placeholder={'Enter AMIC Agency Number'}
                    fullWidth
                    error={!!fieldErrors.artAgentID}
                    helperText={fieldErrors.artAgentID}
                  />
                  <Box mb={4} />
                </Box>

                <Feature allowed={featureFlags.ACCESS_SUPER_ADMIN}>
                  <Box px={1}>
                    <TextField
                      size="small"
                      disabled={USER_ADMIN_EDIT_VIEW}
                      id={'mod-id'}
                      name={'modId'}
                      value={formData.modId}
                      label={'MOD ID'}
                      onChange={(e) => {
                        setFormData({ ...formData, modId: e.target.value });
                        validateField(e, (error) => setFieldErrors((prevState) => ({ ...prevState, modId: error })));
                      }}
                      placeholder={'Enter MOD ID'}
                      fullWidth
                      error={!!fieldErrors.modId}
                      helperText={fieldErrors.modId}
                    />
                    <Box mb={4} />
                  </Box>
                </Feature>
                {USER_ADMIN_ADD_VIEW && (
                  <>
                    <Feature allowed={featureFlags.HAS_VIEW_ALL}>
                      <FormControlLabel
                        checked={formData.ininetViewAll}
                        control={<Checkbox color="primary" />}
                        label={
                          <span>
                            <span style={{ color: 'red' }}>(Internal Only)</span> View All
                          </span>
                        }
                        onClick={handleClickInternalUser}
                        labelPlacement="start"
                      />
                      {alerts.viewAll && <Alert severity="warning">Allows access to ALL agency codes</Alert>}

                      <Box p={1} pt={1.5}>
                        <TextField
                          size="small"
                          id={'agency-name'}
                          name={agencyNameInfo}
                          value={agencyNameInfo}
                          onChange={(e) => {
                            setAgencyNameSearchValue(e?.target.value);
                          }}
                          disabled={formData.ininetViewAll}
                          label={'Agency Name'}
                          placeholder={'Enter Agency Name'}
                          fullWidth
                        />
                      </Box>

                      <Box p={1}>
                        <Button onClick={handleSearchAgencyNameOnClick} id={'search-button'} disabled={formData.ininetViewAll}>
                          Search
                        </Button>
                        <Box display="inline" mr={2} />
                        <Button onClick={handleClearAgencyNameOnClick} variant={'outlined'} color={'secondary'} id={'clearAGN-button'}>
                          Clear
                        </Button>
                      </Box>
                    </Feature>

                    {externalUserAgencies && (
                      <>
                        <Feature forbidden={featureFlags.ACCESS_VIEW_ALL}>
                          <Box p={1} display="flex">
                            <Box ml={2}>
                              <Typography gutterBottom component="div">
                                <Box textAlign={'right'} mr={2}>
                                  Agency Code :
                                </Box>
                              </Typography>
                            </Box>
                            <Select
                              multiple
                              native
                              value={agenciesSelected}
                              onChange={handleExternalAgencyMultipleSelection}
                              inputProps={{
                                id: 'user-admin-add-group-externalUser-all-groups',
                                style: { height: '100px' },
                              }}>
                              {externalUserAgencies.map((agencyId) => (
                                <option id={`option-${agencyId}`} key={agencyId} value={agencyId}>
                                  {agencyId}
                                </option>
                              ))}
                            </Select>
                          </Box>
                        </Feature>
                      </>
                    )}

                    {alerts.searchAgency && <Alert severity="error">Unable to retrieve agencies.</Alert>}
                  </>
                )}
              </Box>
            </Paper>
          </Col>

          <Col xs={6}>
            <Paper variant="outlined" square style={{ boxSizing: 'border-box', height: '100%' }}>
              <Box p={2}>
                <Box p={1}>
                  <Paper variant="outlined" square>
                    <Box p={2}>
                      <Typography variant="h5">Challenge Question and License Information</Typography>
                    </Box>
                  </Paper>
                  <Box mb={2} />
                  <Select
                    label="Challenge Question"
                    labelId="challenge-question"
                    id={'challenge-question'}
                    name={'challengeQuestion'}
                    value={formData.challengeQuestion}
                    onChange={handleSelectOnChange}
                    menuItems={challengeQuestionOptions}
                  />
                  <Box mb={2} />
                  <TextField
                    size="small"
                    id={'challenge-question-response'}
                    value={formData.challengeAnswer}
                    onChange={(e) => {
                      setFormData({ ...formData, challengeAnswer: e.target.value });
                      validateField(e, (error) => setFieldErrors((prevState) => ({ ...prevState, challengeAnswer: error })));
                    }}
                    label={'Challenge Response'}
                    placeholder={'Enter Challenge Response'}
                    name={'challengeAnswer'}
                    required
                    fullWidth
                    error={!!fieldErrors.challengeAnswer}
                    helperText={fieldErrors.challengeAnswer}
                  />
                  <Box mb={4} />
                  <Select
                    label="License Information"
                    labelId="license-information"
                    id={'license-information'}
                    name={'npnStatus'}
                    value={formData.npnStatus}
                    onChange={(e) => {
                      handleSelectOnChange(e);
                      setFormData((prevState) => ({ ...prevState, npnNumber: '' }));
                      setFieldErrors((prevState) => ({ ...prevState, npnStatus: null, npnNumber: null }));
                    }}
                    menuItems={NPN_LICENSE_INFORMATION_OPTIONS}
                    error={!!fieldErrors.npnStatus}
                    helperText={fieldErrors.npnStatus}
                  />
                  {formData.npnStatus === NPN_LICENSE_INFORMATION_OPTIONS[0].value && (
                    <>
                      <Box mb={2} />
                      <TextField
                        size="small"
                        id={'license-information-response'}
                        value={formData.npnNumber}
                        onChange={(e) => {
                          setFormData({ ...formData, npnNumber: e.target.value });
                          validateField(e, (error) => setFieldErrors((prevState) => ({ ...prevState, npnNumber: error })));
                        }}
                        label={'NPN Number'}
                        placeholder={'Enter NPN Number'}
                        name={'npnNumber'}
                        required
                        fullWidth
                        error={!!fieldErrors.npnNumber}
                        helperText={fieldErrors.npnNumber}
                      />
                    </>
                  )}
                </Box>
              </Box>
            </Paper>
          </Col>
        </Row>

        <Feature allowed={featureFlags.HAS_VIEW_ALL}>
          {potentialAgencies && (
            <>
              <Box py={1} mt={1}>
                <Typography variant="h6">Potential Agencies</Typography>
              </Box>
              <Box py={1} style={{ height: 400, overflow: 'auto' }}>
                <DataGrid
                  rows={potentialAgencies.map((agency) => ({
                    ...agency,
                    id: agency.agencyId,
                  }))}
                  columns={potentialAgenciesColumns}
                  pageSize={25}
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  checkboxSelection
                  disableSelectionOnClick
                  selectionModel={selectionModel}
                  onSelectionModelChange={(e) => handleSelectionModelOnChange(e)}
                />
              </Box>
            </>
          )}
        </Feature>

        <Row spacing={3}>
          <Col xs={6}>
            <Paper variant="outlined" square style={{ boxSizing: 'border-box', height: '100%' }}>
              <Box p={3}>
                <Paper variant="outlined" square>
                  <Box p={2}>
                    <Typography variant="h5">Username</Typography>
                  </Box>
                </Paper>
                <Box mb={2} />
                {USER_ADMIN_ADD_VIEW && <Typography>The Username and password will be generated by the application when the form is submitted.</Typography>}
                {USER_ADMIN_EDIT_VIEW && (
                  <>
                    <Box mb={1}>
                      <Typography align="justify" color="textSecondary" display="inline">
                        User Name:{' '}
                      </Typography>
                      <Typography align="justify" color="primary" display="inline" variant="subtitle">
                        {userDetails?.name}
                      </Typography>
                    </Box>
                    <Box mb={1}>
                      <Typography align="justify" color="textSecondary" display="inline">
                        Pass. Exp. Date:{' '}
                      </Typography>
                      <Typography align="justify" color="primary" display="inline" variant="subtitle">
                        {userDetails?.pwdExpDate}
                      </Typography>
                    </Box>
                    <Box mb={2}>
                      <Typography align="justify" color="textSecondary" display="inline">
                        Locked Out?{' '}
                      </Typography>
                      <Typography align="justify" color="inherit" display="inline" variant="subtitle">
                        {userDetails?.accountLockedOut ? 'Yes' : 'No'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography align="justify" color="textSecondary" display="inline">
                        Logon Disabled:{' '}
                      </Typography>
                      <Checkbox
                        align="justify"
                        name="accountDisabled"
                        checked={formData?.accountDisabled ?? false}
                        color="primary"
                        display="inline"
                        onChange={(e) => {
                          setFormData((prevState) => ({ ...prevState, accountDisabled: e.target.checked }));
                        }}
                      />
                    </Box>
                    <Box p={3} align="center">
                      <Button onClick={handleResetPasswordOnClick} id={'reset-password-button'}>
                        Reset Password
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            </Paper>
          </Col>

          <Col xs={6}>
            <Paper variant="outlined" square style={{ boxSizing: 'border-box', height: '100%' }}>
              {USER_ADMIN_ADD_VIEW && (
                <Box p={3}>
                  <Paper variant="outlined" square>
                    <Box p={2}>
                      <Typography variant="h5">Group Information</Typography>
                    </Box>
                  </Paper>
                  <Box mb={2} />
                  <AOTransferList
                    checked={checkedUserGroups}
                    label={'Groups to Add'}
                    left={availableUserGroups}
                    leftLabel={'Available Groups'}
                    right={selectedUserGroups}
                    rightLabel={'Selected Groups'}
                    setLeft={(e) => handleUserGroupsSetLeft(e)}
                    setRight={(e) => handleUserGroupsSetRight(e)}
                    setChecked={setCheckedUserGroups}>
                    required
                  </AOTransferList>
                </Box>
              )}
            </Paper>
          </Col>
        </Row>

        {USER_ADMIN_ADD_VIEW && (
          <Alert severity="info">
            The Agency acknowledges that the individual being added is licensed and appointed to write business with MSA. If you need to complete the
            appointment process, please complete <Link href="https://brandfolder.com/s/rrswnrv96bcj9594tj9jw5z">this form</Link> and return to{' '}
            <Link>FOSCLicensing@MSAGROUP.COM</Link>
          </Alert>
        )}
        {alerts.nullAgency && <Alert severity="warning">Either View All needs to be checked or at least one Agency must be selected before proceeding.</Alert>}
        {alerts.nullGroup && <Alert severity="warning">Group Membership is a required field. Please enter it now.</Alert>}
        {alerts.licenseAlert && <Alert severity="warning">License Information is a required field. Please enter it now.</Alert>}
        {alerts.submitAlert && <Alert severity={alerts.submitAlert.severity}>{alerts.submitAlert.message}</Alert>}
        {alerts.passwordReset && <Alert severity={alerts.passwordReset.severity}>{alerts.passwordReset.message}</Alert>}

        <Box py={2} display="flex" justifyContent="end">
          <Button
            type="button"
            onClick={USER_ADMIN_ADD_VIEW ? handleAddUserSubmitOnClick : handleEditUserSubmitOnClick}
            id={'submit-button'}
            disabled={isSubmitDisabled()}>
            Submit
          </Button>
          <Box p={1} display="inline" />
          <Button
            onClick={USER_ADMIN_ADD_VIEW ? handleAddUserClearFormOnClick : handleEditUserClearFormOnClick}
            variant={'outlined'}
            color={'secondary'}
            id={'clear-button'}>
            Clear
          </Button>
        </Box>
      </Card>
    </Feature>
  );
};

UserAdminAddEditUserInfo.propTypes = {
  title: PropTypes.string,
  userDetails: PropTypes.object,
  setUserDetails: PropTypes.func,
};

export default UserAdminAddEditUserInfo;
